import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import { normalizeWorldCityRow } from "../../lib/worldCity"

/** GET /clients/inbox/users/:userId — properties available in inbox settings. */
export const fetchInboxClientsByUserId = createAsyncThunk(
  "inboxClients/fetchByUserId",
  async (userId, { rejectWithValue }) => {
    try {
      const id = userId != null ? String(userId).trim() : ""
      if (!id) return rejectWithValue("User id is required to fetch inbox clients.")

      const data = await get(`clients/inbox/users/${id}`)
      return Array.isArray(data) ? data : []
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch inbox clients")
    }
  },
)

export function normalizeInboxClient(client) {
  if (!client || client.id == null || client.id === "") return null
  return {
    id: String(client.id),
    name: client.name || "Unnamed",
    websiteUrl: client.websiteUrl ?? "",
    timeZone: client.timeZone ?? null,
    worldCity: normalizeWorldCityRow(client.worldCity),
    socialHandle: client.socialHandle ?? "",
    subBrand: client.subBrand ?? null,
  }
}

export const selectInboxProperties = (state) => state.inboxClients?.clients ?? []

const inboxClientsSlice = createSlice({
  name: "inboxClients",
  initialState: {
    clients: [],
    status: "idle",
    error: null,
    fetchedUserId: null,
  },
  reducers: {
    clearInboxClients(state) {
      state.clients = []
      state.status = "idle"
      state.error = null
      state.fetchedUserId = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInboxClientsByUserId.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchInboxClientsByUserId.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.fetchedUserId =
          action.meta.arg != null ? String(action.meta.arg) : null
        state.clients = (action.payload || [])
          .map(normalizeInboxClient)
          .filter(Boolean)
      })
      .addCase(fetchInboxClientsByUserId.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch inbox clients"
        state.clients = []
      })
  },
})

export const { clearInboxClients } = inboxClientsSlice.actions
export default inboxClientsSlice.reducer
